import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchTranslations } from '../../ducks/translations.duck';
import { fetchCurrentUser, updateUserProfile } from '../../ducks/user.duck';
import { storableError } from '../../util/errors';
import { getLocaleFromPath } from '../../util/locale';
import { logError } from '../../util/log';
import reducerRegistry from '../../ducks/reducerRegistry';

// ================ Action types ================ //

export const FETCH_LISTINGS_FOR_BOOKMARKS_REQUEST =
  'app/BookmarkPage/FETCH_LISTINGS_FOR_BOOKMARKS_REQUEST';
export const FETCH_LISTINGS_FOR_BOOKMARKS_SUCCESS =
  'app/BookmarkPage/FETCH_LISTINGS_FOR_BOOKMARKS_SUCCESS';
export const FETCH_LISTINGS_FOR_BOOKMARKS_ERROR =
  'app/BookmarkPage/FETCH_LISTINGS_FOR_BOOKMARKS_ERROR';

export const MAP_SET_ACTIVE_LISTING = 'app/BookmarkPage/MAP_SET_ACTIVE_LISTING';

// ================ Reducer ================ //

/** @type {import('../../../types/vansite').VanSite.State.BookmarkPage} */
const initialState = {
  fetchListingsForBookmarksInProgress: false,
  fetchListingsForBookmarksError: null,
  bookmarkedListings: [],
};

export default function bookmarkPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_LISTINGS_FOR_BOOKMARKS_REQUEST:
      return {
        ...state,
        fetchListingsForBookmarksError: null,
        fetchListingsForBookmarksInProgress: true,
      };
    case FETCH_LISTINGS_FOR_BOOKMARKS_SUCCESS:
      return { ...state, bookmarkedListings: payload, fetchListingsForBookmarksInProgress: false };
    case FETCH_LISTINGS_FOR_BOOKMARKS_ERROR:
      return {
        ...state,
        fetchListingsForBookmarksError: payload,
        fetchListingsForBookmarksInProgress: false,
      };
    case MAP_SET_ACTIVE_LISTING:
      return {
        ...state,
        activeListingId: payload,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchListingsForBookmarksRequest = () => ({
  type: FETCH_LISTINGS_FOR_BOOKMARKS_REQUEST,
});

export const fetchListingsForBookmarksSuccess = (bookmarkedListings) => ({
  type: FETCH_LISTINGS_FOR_BOOKMARKS_SUCCESS,
  payload: bookmarkedListings,
});

export const fetchListingsForBookmarksError = (error) => ({
  type: FETCH_LISTINGS_FOR_BOOKMARKS_ERROR,
  payload: error,
  error: true,
});

export const setActiveListing = (listingId) => ({
  type: MAP_SET_ACTIVE_LISTING,
  payload: listingId,
});

// ================ Selectors ================ //

export const generateGeoLocationJsonForBookmarks = (bookmarks) => {
  const geoJson = {
    type: 'FeatureCollection',
    features: [],
  };
  for (const bookmark of bookmarks) {
    const geoLocation = bookmark.attributes.geolocation;
    if (geoLocation) {
      const { lat, lng } = geoLocation;
      const feature = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [lng, lat],
        },
      };
      geoJson.features.push(feature);
    }
  }
  return geoJson;
};

const chunkArray = (array, chunkSize = 100) => {
  const chunks = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    // Slice the array from the current index to the current index + chunkSize
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
};

// ================ Thunks ================ //

const BOOKMARKED_LISTING_QUERY_PARAMS = {
  include: ['author', 'images'],
  'fields.listing': [
    'title',
    'geolocation',
    'price',
    'createdAt',
    'publicData.category',
    'publicData.locationPlace',
    'publicData.verified',
    'metadata.rating',
    'metadata.ratingCount',
  ],
  'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
  'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
  'limit.images': 1,
};

// One listing query plus one translation fetch per chunk, all started at once.
const queryBookmarkedListings = (sdk, dispatch, chunks, locale) =>
  chunks.flatMap((ids) => [
    sdk.listings.query({ ids, ...BOOKMARKED_LISTING_QUERY_PARAMS }, { expand: true }),
    dispatch(fetchTranslations({ listing: { ids } }, locale)),
  ]);

// Folds the per-chunk listing responses into a single { data, included } payload.
// Translation responses carry no `data.data` and drop out here.
const mergeListingResponses = (responses) =>
  responses
    .filter((r) => r?.data?.data && r.data.data.length > 0)
    .map((r) => r.data)
    .reduce(
      (agr, res) => ({
        data: [...agr.data, ...res.data],
        included: [...agr.included, ...res.included],
      }),
      { data: [], included: [] }
    );

const hasStaleBookmarks = (currentBookmarks, foundListingIds) => {
  const found = new Set(foundListingIds);
  return currentBookmarks.some((id) => !found.has(id));
};

export const fetchListingsForBookmarks = (locale) => async (dispatch, getState, sdk) => {
  dispatch(fetchListingsForBookmarksRequest());

  try {
    await dispatch(fetchCurrentUser());
    const { currentBookmarks } = getState().bookmarks;

    if (!currentBookmarks || currentBookmarks.length === 0) {
      dispatch(fetchListingsForBookmarksSuccess([]));
      return Promise.resolve([]);
    }

    const responses = await Promise.all(
      queryBookmarkedListings(sdk, dispatch, chunkArray(currentBookmarks), locale)
    );

    const foundListingResponses = mergeListingResponses(responses);
    const foundListingIds = foundListingResponses.data.map((r) => r.id.uuid);

    // Bookmarks pointing at deleted/closed listings are dropped from the profile.
    if (hasStaleBookmarks(currentBookmarks, foundListingIds)) {
      dispatch(updateUserProfile({ privateData: { bookmarks: foundListingIds } }));
    }

    dispatch(addMarketplaceEntities({ data: foundListingResponses }));
    dispatch(fetchListingsForBookmarksSuccess(foundListingIds));
  } catch (e) {
    logError(e, 'fetch-listings-for-bookmarks-failed');
    dispatch(fetchListingsForBookmarksError(storableError(e)));
  }
};

// loadData is a collection of async calls that need to be made
// before page has all the info it needs to render itself
export const loadData = (params, search, match) => (dispatch) => {
  const locale = getLocaleFromPath(match.path);
  return Promise.all([dispatch(fetchListingsForBookmarks(locale))]);
};

// Injected lazily: the reducer reaches the store when this (code-split) module is evaluated.
reducerRegistry.register('BookmarkPage', bookmarkPageReducer);
